import * as yup from 'yup';
import { EarmarkDTO, OwnerModelDTO, OwnerModelFormDTO, OwnerTypeDTO } from '../../OtherAssets.meta';
import { BusinessAssetDTO, BusinessSaleScheduleDTO } from '../../tables/Business/Business.meta';
import {
  EXCLUDE_OPT,
  getCustomBucketIndex,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';

export interface BusinessAssetsFormValues {
  name: string;
  owner: string | null;
  value: number | null;
  yearlyIncome: number | null;
  assetGrowth: number | null;
  saleYear: string | null;
  incomeGrowth: number | null;
  assetEarmarks: { value: string; label: string }[];
  selfEmployed: boolean;
  BusinessSaleSchedule: BusinessSaleScheduleDTO[];
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
  ownerModel: OwnerModelFormDTO[];
  useUnderlyingAssetsForValue: boolean;
}

export interface BusinessAssetsFormDTO {
  Name: string;
  Owner: string;
  Value: number | null;
  YearlyIncome: number | null;
  AssetGrowth: number | null;
  SaleYear: string | null;
  IsSoldAtRetirement: boolean;
  IncomeGrowth: number | null;
  AssetEarmark: EarmarkDTO[];
  SelfEmployed: boolean;
  BusinessSaleSchedule: BusinessSaleScheduleDTO[];
  CustomBucketIndex: number | null;
  IsExcludedFromAssetBucketing: boolean;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
  OrionManagementStyleId: number | null;
  OwnerModel: OwnerModelDTO[];
  UseUnderlyingAssetsForValue: boolean;
}

export interface UpsertBusinessAssetDTO extends BusinessAssetsFormDTO {
  clientId: string;
  Id: string | null;
}

interface DataToForm {
  data: BusinessAssetDTO | null;
  options: OtherAssetsDTO['Options'];
}

export const dataToForm = ({ data, options }: DataToForm): BusinessAssetsFormValues =>
  data
    ? {
        name: data.Name,
        owner: data.Owner,
        value: data.Value,
        yearlyIncome: data.YearlyIncome,
        assetGrowth: data.AssetGrowth,
        saleYear:
          data.SaleYear !== null
            ? data.SaleYear.toString()
            : data.IsSoldAtRetirement
            ? 'At Retirement'
            : null,
        incomeGrowth: data.IncomeGrowth,
        assetEarmarks: data.AssetEarmark.map(({ Type, Tooltip }) => ({
          value: Type,
          label: Tooltip,
        })),
        selfEmployed: data.SelfEmployed || false,
        BusinessSaleSchedule: data.BusinessSaleSchedule,
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: data.OrionManagementStyleId,
          customBucketIndex: data.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: false,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        isLiquid: data.IsLiquid,
        includeInGrossEstate: data.IncludeInGrossEstate,
        orionManagementStyleId: data.OrionManagementStyleId,
        ownerModel: data.OwnerModel.map(owner => ({
          ...owner,
          Percentage: (owner.Percentage * 100).toFixed(2),
        })),
        useUnderlyingAssetsForValue: data.UseUnderlyingAssetsForValue,
      }
    : {
        name: '',
        owner: '',
        value: null,
        yearlyIncome: null,
        assetGrowth: null,
        saleYear: null,
        incomeGrowth: null,
        assetEarmarks: [],
        selfEmployed: false,
        BusinessSaleSchedule: [
          {
            YearsAfterSaleYear: 0,
            SaleAmount: null,
            NetWorthPercent: null,
          },
        ],
        customBucketIndex: null,
        isLiquid: options.BusinessAssetOptions.IsLiquid,
        includeInGrossEstate: true,
        orionManagementStyleId: null,
        ownerModel: [
          {
            Id: null,
            Name: options.BusinessAssetOptions.ownerOptions.People[0].Name,
            OwnerType: OwnerTypeDTO.Principal,
            Percentage: '100',
          },
        ],
        useUnderlyingAssetsForValue: false,
      };

export const formToBody = (values: BusinessAssetsFormValues): BusinessAssetsFormDTO => ({
  Name: values.name,
  Owner: values.owner || '',
  Value: values.value,
  YearlyIncome: values.yearlyIncome,
  AssetGrowth: values.assetGrowth === null ? 0 : values.assetGrowth,
  SaleYear: values.saleYear === 'At Retirement' ? null : values.saleYear,
  IsSoldAtRetirement: values.saleYear === 'At Retirement',
  IncomeGrowth: values.incomeGrowth,
  AssetEarmark: values.assetEarmarks.map(({ value, label }) => ({ Type: value, Tooltip: label })),
  SelfEmployed: values.selfEmployed,
  BusinessSaleSchedule: values.BusinessSaleSchedule,
  CustomBucketIndex:
    values.customBucketIndex === EXCLUDE_OPT.value ? null : values.customBucketIndex,
  IsExcludedFromAssetBucketing: values.customBucketIndex === EXCLUDE_OPT.value,
  IsLiquid: values.isLiquid,
  IncludeInGrossEstate: values.includeInGrossEstate,
  OrionManagementStyleId: values.orionManagementStyleId,
  OwnerModel: values.ownerModel
    .filter(b => b.Name && b.Percentage)
    .map(owner => ({
      ...owner,
      Id: owner.Id ?? null,
      Name: owner.Name ?? '',
      OwnerType: owner.OwnerType ?? 0,
      Percentage: Number((parseFloat(owner.Percentage ?? '0') / 100).toFixed(4)),
    })),
  UseUnderlyingAssetsForValue: values.useUnderlyingAssetsForValue,
});

export const FORM_SCHEMA = yup.object().shape({
  name: yup.string().required('Please specify a name'),
  value: yup
    .number()
    .nullable()
    .when('useUnderlyingAssetsForValue', {
      is: false,
      then: yup
        .number()
        .nullable()
        .notOneOf([0], 'Please specify a valid value for this business')
        .required('Please provide a value for this business'),
      otherwise: yup
        .number()
        .nullable()
        .notRequired(),
    }),
  ownerModel: yup
    .array()
    .test(
      'check-total',
      'The total ownership percentage must add up to 100%',
      (ownerModel, ctx) => {
        if (!ownerModel || !Array.isArray(ownerModel)) return false;
        if (ownerModel[0].Name === '') {
          return ctx.createError({
            path: 'ownerModel[0].Id',
            message: 'Please specify an account owner',
          });
        }
        const sum = ownerModel.reduce((acc, obj) => {
          if (obj.Name && obj.Percentage) {
            return acc + (parseFloat(obj.Percentage) || 0);
          }
          return acc;
        }, 0);
        if (sum !== 100) {
          return ctx.createError({
            path: 'ownerModel[0].Id',
            message: 'The total ownership percentage must add up to 100%',
          });
        }
        return sum === 100;
      },
    ),
});
